import { useState } from 'react';

import styled, { CSSObject } from 'styled-components';

import { Tag, TAG_VARIANTS, TagProps } from '@components/tag';
import { theme } from '@styles/theme';
import { MakePropertiesOptional } from '@utils/types';

type TagSelectorProps = Pick<MakePropertiesOptional<TagProps>, 'variant' | 'size'> & {
  options: Array<string>;
  selectedVariant?: (typeof TAG_VARIANTS)[number];
  selectedTags?: Array<string>;
  onClick: (tag: string) => void;
};

const TagsStyles: CSSObject = {
  cursor: 'pointer',
};

const TagSelector = ({
  options,
  variant = 'purple',
  selectedVariant = 'blue',
  onClick,
  selectedTags = [],
  size = 'large',
}: TagSelectorProps) => {
  return (
    <TagSelectorContainer>
      {options.map((option) => {
        const isSelected = selectedTags.includes(option);
        return (
          <Tag
            key={option}
            size={size}
            variant={isSelected ? selectedVariant : variant}
            text={option}
            onClick={() => onClick(option)}
            styles={TagsStyles}
          />
        );
      })}
    </TagSelectorContainer>
  );
};

export type TagSelectorMultiProps = Omit<TagSelectorProps, 'onClick'> & {
  defaultValue?: Array<string>;
  onChange: (tags: Array<string>) => void;
};

export const TagSelectorMulti = ({
  onChange,
  defaultValue = [],
  ...rest
}: TagSelectorMultiProps) => {
  const [selectedTags, setSelectedTags] = useState<string[]>(defaultValue);

  const handleTagClick = (option: string) => {
    const newTags = selectedTags.includes(option)
      ? selectedTags.filter((tag) => tag !== option)
      : [...selectedTags, option];
    setSelectedTags(newTags);
    onChange(newTags);
  };

  return <TagSelector {...rest} selectedTags={selectedTags} onClick={handleTagClick} />;
};

export type TagSelectorSingleProps = Omit<TagSelectorProps, 'onClick'> & {
  defaultValue?: string;
  onChange: (tag: string) => void;
};

export const TagSelectorSingle = ({
  defaultValue = '',
  onChange,
  ...rest
}: TagSelectorSingleProps) => {
  const [selected, setSelected] = useState(defaultValue);

  const handleTagClick = (option: string) => {
    setSelected(option);
    onChange(option);
  };

  return (
    <TagSelector {...rest} selectedTags={selected ? [selected] : []} onClick={handleTagClick} />
  );
};

const TagSelectorContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.space[2],
});
